import React from "react";
import { defaults, crudAll, CRUDStringLists, ResourceDescriptor, SelectOption } from "./common";
import { Link } from "react-router-dom";
import { formatDurationHours } from "../components/DurationField";
import DeleteResourceButton from "../components/DeleteResourceButton";
import NotSetValue from "../components/NotSetValue";
import { defineMessages, FormattedMessage, MessageDescriptor } from "react-intl";
import { isInternalClient } from "../tools";

const ldapFeatureFilter = (array: string[]) => {
    if (window.features.ldap) {
        return array;
    } else {
        return array.filter((item) => item !== "ldap_auth");
    }
};

function generateOrders(): CRUDStringLists {
    if (global.identityProvider === "internal") {
        return {
            list: ldapFeatureFilter(["display_name", "redirect_uris", "ldap_auth", "controlHeaderCell"]),
            update: ldapFeatureFilter([
                "display_name",
                "identifier",
                "secret",
                "redirect_uris",
                "ldap_auth",
                "refresh_token_expires_in",
                "token_endpoint_auth_method",
                "grant_types",
                "response_types",
                "api_access",
            ]),
        };
    }
    return {
        list: ["display_name", "controlHeaderCell"],
        update: ["display_name", "identifier", "secret", "api_access"],
    };
}

const tokenEndpointAuthMethodTranslations = defineMessages({
    none: {
        id: "client.token_endpoint_auth_methods.none",
        defaultMessage: "None (public client)",
    },
    client_secret_basic: {
        id: "client.token_endpoint_auth_methods.client_secret_basic",
        defaultMessage: "Client secret (HTTP Basic)",
    },
    client_secret_post: {
        id: "client.token_endpoint_auth_methods.client_secret_post",
        defaultMessage: "Client secret (request body)",
    },
});

const grantTypeTranslations = defineMessages({
    authorization_code: {
        id: "client.grant_type_values.authorization_code",
        defaultMessage: "Authorization code",
    },
    refresh_token: {
        id: "client.grant_type_values.refresh_token",
        defaultMessage: "Refresh token",
    },
    password: {
        id: "client.grant_type_values.password",
        defaultMessage: "Password (ROPC)",
    },
});

const responseTypeTranslations = defineMessages({
    code: {
        id: "client.response_type_values.code",
        defaultMessage: "Code",
    },
});

/// Renders a stored value with its translation - or as it is, should the server know a value we do not.
const renderTranslated = (translations: Record<string, MessageDescriptor>, value: string) =>
    value in translations ? <FormattedMessage key={value} {...translations[value]} /> : value;

const renderTranslatedList = (translations: Record<string, MessageDescriptor>, values: string[] | null) => {
    if (!values?.length) return <NotSetValue />;
    return values.map((value, index) => (
        <React.Fragment key={value}>
            {index > 0 && ", "}
            {renderTranslated(translations, value)}
        </React.Fragment>
    ));
};

const translationKeyOf = (translations: Record<string, MessageDescriptor>) => (value: string) =>
    translations[value]?.id ?? value;

const client: ResourceDescriptor = {
    url: "clients",
    orders: generateOrders(),
    crudActions: crudAll,
    listDefaults: defaults.list,
    optionsFormatter: (row: any): SelectOption => {
        return { value: row.id, text: row.display_name };
    },
    renderers: {
        read: {
            redirect_uris: (val, _row) => {
                // jscs:ignore requireCamelCaseOrUpperCaseIdentifiers
                if (val.length == 1) return val[0];
                const list = [];
                for (const v of val) list.push(<li>{v}</li>);
                return <ul>{...list}</ul>;
            },
            ldap_auth: (val, _row) => {
                if (val) return <span>{`${val.host}:${val.port}`}</span>;
                else return <NotSetValue />;
            },
            refresh_token_expires_in: (val, _row) => {
                return formatDurationHours(val);
            },
            token_endpoint_auth_method: (val, _row) => renderTranslated(tokenEndpointAuthMethodTranslations, val),
            grant_types: (val, _row) => renderTranslatedList(grantTypeTranslations, val),
            response_types: (val, _row) => renderTranslatedList(responseTypeTranslations, val),
            api_access: (val, _row) =>
                val ? (
                    <FormattedMessage id="yes" defaultMessage="Yes" />
                ) : (
                    <FormattedMessage id="no" defaultMessage="No" />
                ),
        },
        list: {
            ldap_auth: (val, _row) => {
                // jscs:ignore requireCamelCaseOrUpperCaseIdentifiers
                if (val) return `${val.host}:${val.port}`; // Maybe TODO: put a link to ldap read page
                else return <NotSetValue />;
            },
            redirect_uris: (val, _row) => {
                // jscs:ignore requireCamelCaseOrUpperCaseIdentifiers
                return val ? val.join(", ") : "";
            },
            display_name: (val, row) => {
                return <Link to={`/clients/read/${row.id}`}>{val}</Link>;
            },
            controlHeaderCell: (val, row) => {
                const isInternal = isInternalClient(row.id);
                return (
                    <div>
                        <DeleteResourceButton
                            id={row.id}
                            disabled={isInternal}
                            tooltip={
                                // <FormattedMessage id={isInternal ? "client.delete_disabled_self" : "client.delete"} />
                                isInternal ? (
                                    <FormattedMessage
                                        id="client.delete_disabled_self"
                                        defaultMessage="Internal client, delete disabled"
                                    />
                                ) : (
                                    <FormattedMessage id="client.delete" defaultMessage="Delete client" />
                                )
                            }
                            resourceName="client"
                        />
                    </div>
                );
            },
        },
    },
    expand: {
        read: ldapFeatureFilter([
            "ldap_auth",
            "roles",
            "roles.client_role_mapping_rules.context_substitutions",
            "roles.role_contexts.context",
        ]),
        list: window.features.ldap ? ["ldap_auth"] : [],
    },
    enumTranslation: {
        token_endpoint_auth_method: translationKeyOf(tokenEndpointAuthMethodTranslations),
        grant_types: translationKeyOf(grantTypeTranslations),
        response_types: translationKeyOf(responseTypeTranslations),
    },
    identifiers: ["display_name"],
    resourceSchemaPostProcessor: (schema) => {
        const refresh_token_expires_in = schema["refresh_token_expires_in"];
        refresh_token_expires_in.type.name = "Duration";

        if (!window.features.ldap) delete schema["ldap_auth"];
    },
};

export default client;
